<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createSeafileConfig } from "./fs";
  import { makeFileSystemSeafile, seafileLogin } from "./fs-seafile";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createSeafileConfig());
  let password = $state("");
  let code = $state("");
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.url.trim() != "" && config.user.trim() != "" && password != "" && !testingConnection,
  );

  function seafileHost(): string {
    try {
      return new URL(config.url.trim()).host;
    } catch {
      return config.url.trim();
    }
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("logging in to Seafile");
    try {
      config.url = config.url.trim();
      config.user = config.user.trim();
      config.name = config.name.trim();
      config.token = await seafileLogin(config.url, config.user, password, code.trim());
      if (config.name == "") {
        config.name = seafileHost();
      }
      let fs = makeFileSystemSeafile(config);
      let who = config.name;
      telegramNotify(`New Seafile connection for ${who}`);
      captureEvent("seafile-login", { host: seafileHost() });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Seafile">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-seafile-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-seafile-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-seafile-url">Server URL:</label>
      <input
        type="text"
        id="input-seafile-url"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        placeholder="https://cloud.example.com"
        bind:value={config.url}
      />

      <label class="mt-3" for="input-seafile-user">Email or user name:</label>
      <input
        type="text"
        id="input-seafile-user"
        autocomplete="username"
        class="outline-hidden border px-2 py-1"
        bind:value={config.user}
      />

      <label class="mt-3" for="input-seafile-password">Password:</label>
      <input
        type="password"
        id="input-seafile-password"
        autocomplete="current-password"
        class="outline-hidden border px-2 py-1"
        bind:value={password}
      />

      <label class="mt-3" for="input-seafile-code"
        >Two-factor code: <span class="text-gray-400">(if the account uses one)</span></label
      >
      <input
        type="text"
        id="input-seafile-code"
        inputmode="numeric"
        autocomplete="one-time-code"
        class="outline-hidden border px-2 py-1"
        bind:value={code}
      />

      <div class="mt-3 w-72 text-gray-500">
        The Sumatra File Manager Online server logs in to your Seafile server for you. The password is not kept. Sumatra
        File Manager Online keeps the login token in this browser. Encrypted libraries are not shown.
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Seafile</button>
    </div>
  {/snippet}
</DialogBase>
